import { Component, Inject, inject, OnInit, ViewChild } from "@angular/core";
import {
    AsyncPipe,
    CommonModule,
    NgOptimizedImage,
    NgTemplateOutlet,
} from "@angular/common";
import { combineLatest, Observable, startWith, Subject } from "rxjs";
import { map, shareReplay } from "rxjs/operators";
import { UserDm, DismissedBanner } from "@/_models";
import {
    USER_IS_AT_LEAST_ADMIN,
    USER_IS_OWNER,
    UserService,
} from "@/_services";
import { OnboardingGuideService } from "@/_services/onboarding-guide.service";
import { SharedComponentsModule } from "@/components/shared/shared-components.module";
import { NavigationEnd, Router, RouterOutlet } from "@angular/router";
import { HomeAsapPerformanceComponent } from "@/components/home/home-asap-performance/home-asap-performance.component";
import { DropdownModule } from "primeng/dropdown";
import {
    FormControl,
    FormGroup,
    FormsModule,
    ReactiveFormsModule,
} from "@angular/forms";
import { filter, tap } from "rxjs/operators";
import { HazardReportingFeature } from "@features/HazardReportingFeature";
import { AccountService } from "@/_services/account.service";
import { AuthenticationService } from "@/_services/authentication.service";
import { isAsapAccount } from "@/_models/account.interface";
import { OverlayPanelModule } from "primeng/overlaypanel";
import { SortingHomeService } from "@/_services/sorting-home.service";
import { HomeDateFilterComponent } from "./home-date-filter/home-date-filter.component";
import { HomeFilterService, HomeScope } from "@/_services/home-filter.service";
import { TerminologyService } from "@/_services/terminology/terminology.service";
import { TermKey } from "@/_services/terminology/catalog";
import { CombinedTasksService } from "@/_services/combined-task.service";
import { MessagesModule } from "primeng/messages";
import { Button } from "primeng/button";
import UserModel = UserDm.UserModel;
import { QuickStartBannerComponent } from "./quick-start-banner/quick-start-banner.component";
import { PromotionalCardComponent } from "./promotional-card/promotional-card.component";
import {
    ProgramHealthComponent,
    ProgramHealthFilter,
} from "./program-health/program-health.component";
import { AskRiseComponent } from "./ask-rise/ask-rise.component";
import { SafetyPipelineComponent } from "./safety-pipeline/safety-pipeline.component";
import { PerformancePanelComponent } from "./performance-panel/performance-panel.component";
import { SubscriptionService } from "@/_services/subscription.service";
import { StatsService } from "@/_services";
import { UntilDestroy, untilDestroyed } from "@ngneat/until-destroy";
import { FlightReportDetailsComponent } from "@/components/reports/flight-report-details/flight-report-details.component";
import { SalesLinkDirective } from "@/_directives/sales-link.directive";

@UntilDestroy()
@Component({
    selector: "app-home",
    standalone: true,
    imports: [
        SalesLinkDirective,
        AsyncPipe,
        NgOptimizedImage,
        SharedComponentsModule,
        RouterOutlet,
        HomeAsapPerformanceComponent,
        DropdownModule,
        FormsModule,
        OverlayPanelModule,
        NgTemplateOutlet,
        HomeDateFilterComponent,
        CommonModule,
        ReactiveFormsModule,
        MessagesModule,
        Button,
        QuickStartBannerComponent,
        PromotionalCardComponent,
        ProgramHealthComponent,
        SafetyPipelineComponent,
        PerformancePanelComponent,
        AskRiseComponent,
        FlightReportDetailsComponent,
    ],
    templateUrl: "./home.component.html",
    styleUrl: "./home.component.scss",
})
export class HomeComponent implements OnInit {
    private userService = inject(UserService);
    private router = inject(Router);
    private accountService = inject(AccountService);
    private onboardingGuideService = inject(OnboardingGuideService);
    private subscriptionService = inject(SubscriptionService);
    private authService = inject(AuthenticationService);
    private statsService = inject(StatsService);
    public homeFilterService = inject(HomeFilterService); // Make public to use in template
    private terminology = inject(TerminologyService);

    @ViewChild(HomeDateFilterComponent)
    dateFilterComponent: HomeDateFilterComponent;

    currentUser$: Observable<UserModel>;
    canUseTasks$: Observable<boolean>;
    filtersApplied$: Observable<boolean>; // Declare the new observable here
    isTrialAccount$: Observable<boolean>;
    asapFormId$: Observable<string>;

    currentDate: Date;
    isTasksRoute = false;
    filterForm!: FormGroup; // Still reference it for convenience
    appliedDateFilter: string = "next7Days"; // Track the applied filter
    asapFormId: string = "";

    currentQuote: string = "";
    quoteList: string[] = [
        "Safety isn't a rulebook, it's a mindset.",
        "Safety first is safety always.",
        "The safest risk is the one you've planned for.",
        "You can't manage what you don't measure.",
        "The best safety tool is a well-trained mind.",
        "Awareness is the first step toward accident prevention.",
        "Risk management is the art of staying in control.",
        "A culture of safety starts with leadership.",
        "Good safety habits are built one decision at a time.",
        "Consistent safety practices prevent costly mistakes.",
        "Plan ahead—accidents don't give warnings.",
        "Better a thousand safety checks than one fatal mistake.",
        "Predict the risk, prevent the loss.",
        "Protect what matters—your life, your team, your future.",
        "Near misses are warnings—heed them.",
        "Complacency is the enemy of safety.",
        "Safety is not just a priority—it's a core value.",
        "Every risk ignored is a hazard waiting to happen.",
        "Vigilance today prevents disasters tomorrow.",
        "The best defense against risk is preparation.",
    ];

    formType = [
        { label: "All Types", value: null },
        { label: "Assessments", value: "assessment" },
        { label: "Hazards", value: "hazard" },
        { label: "Incidents", value: "incident" },
        { label: "Internal Audit", value: "audits" },
        { label: "Reports", value: "flights" },
        { label: "Promotions", value: "promotions" },
        { label: "Cases", value: "issues" },
        { label: "Tasks", value: "tasks" },
        { label: "Risk Evaluations", value: "riskEvaluations" },
    ];
    selectedFormType: string | null = null; // This might become redundant, consider removing if only using the formGroup directly
    timeFrameControl: FormControl<number> = new FormControl<number>(30);
    // Boolean flag for ASAP accounts for use in templates
    isAsap: boolean = false;
    /**
     * The custom SMS manual offer is an aviation service: it is written around
     * FAA, EASA and Transport Canada, and means nothing to a construction or
     * maritime operation.
     */
    isAviationAccount = false;
    showUpgradeBanner = true;
    showPromotionalCard = false;
    showQuickStart = false;
    hasPastDueItems = false;

    assignedItemsTitle = "";
    scopeMineLabel = "";
    scopeEveryoneLabel = "";
    currentScope: HomeScope = "mine";

    navTabList = [
        { label: "Tasks", link: "tasks" },
        /*{ label: "Mentions", link: "mentions" },*/
        { label: "Promotions", link: "promotions" },
    ];

    // State for the report details sidebar (create mode)
    reportSidebarVisible = false;
    reportSidebarAssessmentId: string | null = null;
    returnToUrl = "";

    constructor(
        @Inject(USER_IS_AT_LEAST_ADMIN)
        public readonly isAdmin$: Observable<boolean>,
        @Inject(USER_IS_OWNER) public readonly isOwner$: Observable<boolean>,
        private sortingService: SortingHomeService,
        private homeService: CombinedTasksService,
    ) {}

    ngOnInit(): void {
        this.statsService.refreshDashboardStats();
        this.currentDate = new Date();
        this.currentUser$ = this.userService.getCurrentUserAsync();
        this.currentUser$.pipe(untilDestroyed(this)).subscribe((user) => {
            this.isAsap = isAsapAccount(user.account);
            this.isAviationAccount = this.authService.isAviationAccount();
        });
        this.currentQuote =
            this.quoteList[Math.floor(Math.random() * this.quoteList.length)];

        this.isTasksRoute = this.router.url.includes("/home/tasks");

        // Subscribe to ASAP Form ID from shared state
        this.asapFormId$ = this.onboardingGuideService.asapFormId$.pipe(
            filter((id) => !!id),
            shareReplay(),
        );

        // Subscribe to route changes to update the flag dynamically
        this.router.events
            .pipe(
                filter((event) => event instanceof NavigationEnd),
                untilDestroyed(this),
            )
            .subscribe(() => {
                const isNowTasksRoute = this.router.url.includes("/home/tasks");

                // If navigating away from tasks, clear filters
                if (this.isTasksRoute && !isNowTasksRoute) {
                    this.clearFilters();
                }

                this.isTasksRoute = isNowTasksRoute;

                if (isNowTasksRoute) {
                    // Re-check past due items when navigating to tasks
                    this.checkPastDueItems();
                }
            });

        this.assignedItemsTitle = this.term("dashboard.assignedItems.title");
        this.scopeMineLabel = this.term("dashboard.assignedItems.scope.mine");
        this.scopeEveryoneLabel = this.term(
            "dashboard.assignedItems.scope.everyone",
        );

        // Assign the filterForm from the service
        this.filterForm = this.homeFilterService.form;

        this.filterForm
            .get("scope")!
            .valueChanges.pipe(untilDestroyed(this))
            .subscribe((scope) => (this.currentScope = scope as HomeScope));

        // Initialize filtersApplied$ as a reactive observable
        this.filtersApplied$ = combineLatest([
            this.homeFilterService.hasDateFilter$, // Observable for date filter status
            this.filterForm
                .get("formType")!
                .valueChanges.pipe(
                    startWith(this.filterForm.get("formType")!.value),
                ), // Observable for formType
            this.filterForm
                .get("pastDue")!
                .valueChanges.pipe(
                    startWith(this.filterForm.get("pastDue")!.value),
                ), // Observable for pastDue
            // Add other controls here if they contribute to the overall filter status
        ]).pipe(
            map(([hasDateFilter, formType, pastDue]) => {
                // Combine all filter states
                return hasDateFilter || !!formType || pastDue;
            }),
        );

        // Load persisted dismiss states from dismissedBanners array
        this.currentUser$.pipe(untilDestroyed(this)).subscribe((user) => {
            if (user?.dismissedBanners) {
                const bannerKey = this.isAsap
                    ? DismissedBanner.AcsfCard
                    : DismissedBanner.ManualPromo;
                this.showPromotionalCard =
                    !user.dismissedBanners.includes(bannerKey);

                this.showUpgradeBanner = !user.dismissedBanners.includes(
                    DismissedBanner.UpgradeBanner,
                );
                this.showQuickStart = !user.dismissedBanners.includes(
                    DismissedBanner.QuickStartHomeSection,
                );
            }
        });

        this.canUseTasks$ = this.accountService
            .isFeatureEnabled(HazardReportingFeature.key)
            .pipe(shareReplay());

        this.isTrialAccount$ = this.subscriptionService.isTrialAccount();

        // Check for past due items
        this.checkPastDueItems();
    }

    checkPastDueItems(): void {
        this.homeService
            .hasPastDueItems()
            .pipe(untilDestroyed(this))
            .subscribe({
                next: (result) => {
                    this.hasPastDueItems = result.hasPastDue;
                },
                error: () => {
                    this.hasPastDueItems = false;
                },
            });
    }

    submitAssessment() {
        this.returnToUrl = this.router.url;
        this.reportSidebarAssessmentId = null;
        this.reportSidebarVisible = true;
    }

    submitReport() {
        this.returnToUrl = this.router.url;
        const asapFormId = this.onboardingGuideService.getAsapFormIdSnapshot();
        this.reportSidebarAssessmentId = this.isAsap ? asapFormId : null;
        this.reportSidebarVisible = true;
    }

    onFilterApplied(filterType: string): void {
        this.appliedDateFilter = filterType;
    }

    isPastDueFilter(): boolean {
        return this.appliedDateFilter === "pastDue";
    }

    onSortChange(event: any) {
        const sorting = { key: "flightDate", value: event.value };
        this.sortingService.updateSorting(sorting);
    }

    clearFilters() {
        this.homeFilterService.resetFilters();
        if (this.dateFilterComponent) {
            this.dateFilterComponent.resetToDefault();
        }
        this.appliedDateFilter = "next7Days";
        // Re-check past due items when filters are cleared
        this.checkPastDueItems();
    }

    // UI helpers for banners/cards
    dismissUpgradeBanner() {
        this.showUpgradeBanner = false;
        this.userService
            .dismissBanner(DismissedBanner.UpgradeBanner, true)
            .subscribe();
    }

    loadPastDueTasks() {
        this.filterForm.patchValue({ pastDue: true });

        if (this.dateFilterComponent) {
            this.dateFilterComponent.setFilterType("pastDue");
        }
        this.appliedDateFilter = "pastDue";
        this.checkPastDueItems();
    }

    setScope(scope: HomeScope) {
        this.filterForm.patchValue({ scope });
    }

    private term(key: string): string {
        return this.terminology.resolve(key as TermKey);
    }

    /**
     * Program Health's counts double as filters. Selecting one moves the work
     * list to everyone's items in that state, so the count, the breakdown and
     * the list all agree; clearing it returns the list to the default.
     */
    onHealthFilterChange(filter: ProgramHealthFilter) {
        if (filter === "overdue") {
            this.loadPastDueTasks();
            return;
        }
        if (filter === null) {
            this.clearFilters();
        }
    }

    createTask() {
        this.accountService
            .checkFormFeature(HazardReportingFeature.key)
            .pipe(
                filter((result) => result),
                tap(() => this.router.navigateByUrl("/home/tasks/new")),
            )
            .subscribe();
    }
}
